import React, { useCallback, useEffect, useState } from "react";
import { Bookmark, Check, Loader2, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { SITEDETAIL } from "@/constants/testIds";

/**
 * Theme Presets — network-wide named 5-tuples of (header, footer,
 * template/variant, palette, font_pair) so an operator who lands on a great
 * look on one site can one-click apply it to any other site.
 *
 * Lives inside the Theme Picker panel as its 7th tab. The CRUD endpoints are
 * at `/api/theme-presets` (admin-only). Save-current captures whatever the
 * site is currently persisting; Apply PATCHes those 6 fields back onto the
 * site in a single request and reloads the parent SiteDetail.
 */

const KINDS = ["header", "footer", "layout", "palette", "font"];

export default function PresetsView({ site, reload }) {
  const [presets, setPresets] = useState(null); // null = loading, [] = loaded empty
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(null); // 'save' | `apply:${id}` | `del:${id}`
  const [flash, setFlash] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Fetch the list on mount + whenever reloadKey ticks (after create/delete).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/theme-presets");
        if (!cancelled) setPresets(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (cancelled) return;
        setPresets([]);
        const msg = (e && e.response && e.response.data && e.response.data.detail) || "Failed to load presets";
        setFlash({ kind: "err", msg });
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  async function saveCurrent() {
    const trimmed = name.trim();
    if (busy || !trimmed) {
      if (!trimmed && !busy) setFlash({ kind: "err", msg: "Name required" });
      return;
    }
    setBusy("save"); setFlash(null);
    try {
      await api.post("/theme-presets", {
        name: trimmed,
        header_variant: site.header_variant,
        footer_variant: site.footer_variant,
        template: site.template,
        variant: site.variant,
        palette: site.palette,
        font_pair: site.font_pair,
      });
      setName("");
      setFlash({ kind: "ok", msg: `Saved "${trimmed}"` });
      refetch();
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.detail) || "Failed to save preset";
      setFlash({ kind: "err", msg });
    } finally {
      setBusy(null);
    }
  }

  async function applyPreset(p) {
    if (busy) return;
    setBusy(`apply:${p.id}`); setFlash(null);
    try {
      await api.patch(`/sites/${site.id}`, {
        header_variant: p.header_variant,
        footer_variant: p.footer_variant,
        template: p.template,
        variant: p.variant,
        palette: p.palette,
        font_pair: p.font_pair,
      });
      setFlash({ kind: "ok", msg: `Applied "${p.name}" to this site` });
      await reload?.();
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.detail) || "Failed to apply preset";
      setFlash({ kind: "err", msg });
    } finally {
      setBusy(null);
    }
  }

  async function deletePreset(p) {
    if (busy) return;
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    setBusy(`del:${p.id}`); setFlash(null);
    try {
      await api.delete(`/theme-presets/${p.id}`);
      setFlash({ kind: "ok", msg: `Deleted "${p.name}"` });
      refetch();
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.detail) || "Failed to delete preset";
      setFlash({ kind: "err", msg });
    } finally {
      setBusy(null);
    }
  }

  const isCurrentSite = (p) =>
    p.header_variant === site.header_variant &&
    p.footer_variant === site.footer_variant &&
    p.template === site.template &&
    p.variant === site.variant &&
    p.palette === site.palette &&
    p.font_pair === site.font_pair;

  return (
    <div className="p-5">
      {/* Save-current form */}
      <div className="border border-zinc-800 rounded-sm p-4 bg-zinc-900/30 mb-5">
        <div className="text-xs text-zinc-400 mb-2 mono">
          Save this site&apos;s current visual config as a reusable preset:&nbsp;
          <span className="text-zinc-500">{site.header_variant} · {site.footer_variant} · {site.template}/{site.variant} · {site.palette} · {site.font_pair}</span>
        </div>
        <div className="flex gap-2 items-center">
          <input
            data-testid={SITEDETAIL.themePresetSaveInput}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") saveCurrent(); }}
            placeholder='e.g. "Bold Magazine Dark"'
            disabled={!!busy}
            className="admin-input flex-1 bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm"
          />
          <button
            data-testid={SITEDETAIL.themePresetSaveButton}
            onClick={saveCurrent}
            disabled={!!busy || !name.trim()}
            className="inline-flex items-center gap-1.5 bg-amber-500/20 border border-amber-500/40 hover:bg-amber-500/30 text-amber-200 disabled:opacity-40 px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider"
          >
            {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Save preset
          </button>
        </div>
      </div>

      {/* Preset list */}
      {presets === null ? (
        <div className="text-center text-xs text-zinc-500 mono py-12">
          <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />
          Loading presets…
        </div>
      ) : presets.length === 0 ? (
        <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center bg-zinc-950/40">
          <Bookmark className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <div className="text-sm text-zinc-400 mb-1">No presets yet.</div>
          <div className="text-xs text-zinc-600 mono">Land on a combination you love (via the Shuffle tab or by tuning the other tabs), then save it here.</div>
        </div>
      ) : (
        <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800 bg-zinc-950/40">
          {presets.map((p) => {
            const active = isCurrentSite(p);
            const busyApply = busy === `apply:${p.id}`;
            const busyDel = busy === `del:${p.id}`;
            return (
              <div
                key={p.id}
                data-testid={SITEDETAIL.themePresetRow(p.id)}
                className={`flex items-center justify-between gap-3 px-4 py-3 ${active ? "bg-amber-500/5" : ""}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-semibold text-zinc-100 truncate">{p.name}</div>
                    {active && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300 bg-amber-500/15 border border-amber-500/40 px-1.5 py-0.5 rounded-sm">
                        Current
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1.5 mt-1.5 flex-wrap">
                    {KINDS.map((k) => (
                      <span
                        key={k}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-300 bg-zinc-800/60 border border-zinc-700 px-1.5 py-0.5 rounded-sm"
                      >
                        <span className="text-zinc-500">{k}</span>
                        <span className="text-zinc-100 normal-case font-bold">
                          {k === "layout" ? `${p.template}/${p.variant}` :
                           k === "font" ? p.font_pair :
                           p[`${k}_variant`] ?? p[k]}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    data-testid={SITEDETAIL.themePresetApply(p.id)}
                    onClick={() => applyPreset(p)}
                    disabled={!!busy || active}
                    className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm transition ${
                      active
                        ? "bg-amber-500/10 text-amber-400/70 cursor-default"
                        : "bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 disabled:opacity-40"
                    }`}
                  >
                    {busyApply ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    {active ? "Current" : "Apply"}
                  </button>
                  <button
                    data-testid={SITEDETAIL.themePresetDelete(p.id)}
                    onClick={() => deletePreset(p)}
                    disabled={!!busy}
                    className="text-xs text-zinc-500 hover:text-rose-400 disabled:opacity-40 inline-flex items-center px-1.5 py-1 rounded-sm hover:bg-rose-500/5"
                    aria-label={`Delete ${p.name}`}
                    title={`Delete ${p.name}`}
                  >
                    {busyDel ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {flash && (
        <div
          data-testid="theme-picker-flash"
          className={`text-xs mono mt-4 ${flash.kind === "ok" ? "text-emerald-400" : "text-rose-400"}`}
        >
          {flash.kind === "ok" ? "✓ " : "✕ "}{flash.msg}
        </div>
      )}
    </div>
  );
}
